import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PageLoader } from './components/LoadingSpinner';
import { useAuthStore } from './store/authStore';

// Lazy load pages for code splitting
const Home = lazy(() => import('./pages/Home').then(m => ({ default: m.HomePage })));
const ProductsPage = lazy(() => import('./pages/ProductsPage').then(m => ({ default: m.ProductsPage })));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage').then(m => ({ default: m.ProductDetailPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(m => ({ default: m.ServicesPage })));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage').then(m => ({ default: m.ServiceDetailPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const NewsPage = lazy(() => import('./pages/NewsPage').then(m => ({ default: m.NewsPage })));
const NewsArticlePage = lazy(() => import('./pages/NewsArticlePage'));
const BecomeDistributorPage = lazy(() => import('./pages/BecomeDistributorPage').then(m => ({ default: m.BecomeDistributorPage })));
const CompensationPlanPage = lazy(() => import('./pages/CompensationPlanPage').then(m => ({ default: m.CompensationPlanPage })));
const BusinessPartnerApplicationPage = lazy(() => import('./pages/BusinessPartnerApplicationPage').then(m => ({ default: m.BusinessPartnerApplicationPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const WarrantyPage = lazy(() => import('./pages/WarrantyPage').then(m => ({ default: m.WarrantyPage })));
const MoneyBackGuaranteePage = lazy(() => import('./pages/MoneyBackGuaranteePage').then(m => ({ default: m.MoneyBackGuaranteePage })));
const SalesPolicyPage = lazy(() => import('./pages/SalesPolicyPage').then(m => ({ default: m.SalesPolicyPage })));
const ShippingPolicyPage = lazy(() => import('./pages/ShippingPolicyPage').then(m => ({ default: m.ShippingPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const ReturnPolicyPage = lazy(() => import('./pages/ReturnPolicyPage').then(m => ({ default: m.ReturnPolicyPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ReportIllegalActivityPage = lazy(() => import('./pages/ReportIllegalActivityPage').then(m => ({ default: m.ReportIllegalActivityPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

// Auth pages
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));

// Cart & Checkout
const Cart = lazy(() => import('./pages/Cart').then(m => ({ default: m.Cart })));
const Checkout = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation').then(m => ({ default: m.OrderConfirmation })));

// Account pages
const AccountLayout = lazy(() => import('./pages/account/AccountLayout').then(m => ({ default: m.AccountLayout })));
const Dashboard = lazy(() => import('./pages/account/Dashboard').then(m => ({ default: m.Dashboard })));
const Orders = lazy(() => import('./pages/account/Orders').then(m => ({ default: m.Orders })));
const OrderDetail = lazy(() => import('./pages/account/OrderDetail').then(m => ({ default: m.OrderDetail })));
const Addresses = lazy(() => import('./pages/account/Addresses').then(m => ({ default: m.Addresses })));
const Wishlist = lazy(() => import('./pages/account/Wishlist').then(m => ({ default: m.Wishlist })));
const Rewards = lazy(() => import('./pages/account/Rewards').then(m => ({ default: m.Rewards })));
const Settings = lazy(() => import('./pages/account/Settings').then(m => ({ default: m.Settings })));
const Bookings = lazy(() => import('./pages/account/Bookings').then(m => ({ default: m.Bookings })));
const Subscriptions = lazy(() => import('./pages/account/Subscriptions').then(m => ({ default: m.Subscriptions })));
const Wallet = lazy(() => import('./pages/account/Wallet').then(m => ({ default: m.Wallet })));
const Notifications = lazy(() => import('./pages/account/Notifications').then(m => ({ default: m.Notifications })));
const WarrantyManagement = lazy(() => import('./pages/account/WarrantyManagement').then(m => ({ default: m.WarrantyManagement })));

// Distributor pages
const DistributorDashboard = lazy(() => import('./pages/distributor/DistributorDashboard').then(m => ({ default: m.DistributorDashboard })));
const Referrals = lazy(() => import('./pages/distributor/Referrals').then(m => ({ default: m.Referrals })));
const Commissions = lazy(() => import('./pages/distributor/Commissions').then(m => ({ default: m.Commissions })));

// Business features
const GiftCardPage = lazy(() => import('./pages/GiftCardPage').then(m => ({ default: m.GiftCardPage })));
const ReferralPage = lazy(() => import('./pages/ReferralPage').then(m => ({ default: m.ReferralPage })));
const DownloadCenterPage = lazy(() => import('./pages/DownloadCenterPage').then(m => ({ default: m.DownloadCenterPage })));

// Educational/Content pages
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })));
const FunctionsPage = lazy(() => import('./pages/FunctionsPage').then(m => ({ default: m.FunctionsPage })));
const ScenariosPage = lazy(() => import('./pages/ScenariosPage').then(m => ({ default: m.ScenariosPage })));
const CommunityPage = lazy(() => import('./pages/CommunityPage').then(m => ({ default: m.CommunityPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (isAuthenticated) {
    return <Navigate to="/account" replace />;
  }
  
  return <>{children}</>;
}



// Wrapper for lazy-loaded pages
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <Home />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/products',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ProductsPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/products/:id',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ProductDetailPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/about',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <AboutPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/cart',
    element: (
      <ErrorBoundary>
        <Layout><Cart /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/checkout',
    element: (
      <ErrorBoundary>
        <Layout>
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/order-confirmation/:orderId',
    element: (
      <ErrorBoundary>
        <Layout>
          <ProtectedRoute>
            <OrderConfirmation />
          </ProtectedRoute>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/services',
    element: (
      <ErrorBoundary>
        <Layout><ServicesPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/services/:id',
    element: (
      <ErrorBoundary>
        <Layout><ServiceDetailPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/account',
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <AccountLayout />
        </ProtectedRoute>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: 'orders',
        element: <Orders />,
      },
      {
        path: 'orders/:orderId',
        element: <OrderDetail />,
      },
      {
        path: 'addresses',
        element: <Addresses />,
      },
      {
        path: 'wishlist',
        element: <Wishlist />,
      },
      {
        path: 'rewards',
        element: <Rewards />,
      },
      {
        path: 'bookings',
        element: <Bookings />,
      },
      {
        path: 'subscriptions',
        element: <Subscriptions />,
      },
      {
        path: 'wallet',
        element: <Wallet />,
      },
      {
        path: 'notifications',
        element: <Notifications />,
      },
      {
        path: 'warranty',
        element: <WarrantyManagement />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
    ],
  },
  {
    path: '/blog',
    element: (
      <ErrorBoundary>
        <Layout><BlogPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/blog/:slug',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <BlogPostPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/news',
    element: (
      <ErrorBoundary>
        <Layout><NewsPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/news/:slug',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <NewsArticlePage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/distributor',
    element: (
      <ErrorBoundary>
        <Layout><BecomeDistributorPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <PrivacyPolicyPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/warranty',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <WarrantyPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/money-back-guarantee',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <MoneyBackGuaranteePage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/sales-policy',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <SalesPolicyPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/shipping-policy',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ShippingPolicyPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/report-illegal-activity',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ReportIllegalActivityPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/terms-of-service',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <TermsOfServicePage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/return-policy',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ReturnPolicyPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/contact',
    element: (
      <ErrorBoundary>
        <Layout><ContactPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/gift-cards',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <GiftCardPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/refer-a-friend',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ReferralPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/knowledge-base',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <KnowledgeBasePage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/functions',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <FunctionsPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/scenarios',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <ScenariosPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/community',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <CommunityPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/downloads',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <DownloadCenterPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/distributor',
    element: (
      <ErrorBoundary>
        <Layout><BecomeDistributorPage /></Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/compensation-plan',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <CompensationPlanPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/business-partner-application',
    element: (
      <ErrorBoundary>
        <Layout>
          <LazyPage>
            <BusinessPartnerApplicationPage />
          </LazyPage>
        </Layout>
      </ErrorBoundary>
    ),
  },
  {
    path: '/distributor/dashboard',
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <DistributorDashboard />
        </ProtectedRoute>
      </ErrorBoundary>
    ),
  },
  {
    path: '/distributor/referrals',
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <Referrals />
        </ProtectedRoute>
      </ErrorBoundary>
    ),
  },
  {
    path: '/distributor/commissions',
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <Commissions />
        </ProtectedRoute>
      </ErrorBoundary>
    ),
  },
  {
    path: '/login',
    element: (
      <ErrorBoundary>
        <GuestRoute>
          <Login />
        </GuestRoute>
      </ErrorBoundary>
    ),
  },
  {
    path: '/register',
    element: (
      <ErrorBoundary>
        <GuestRoute>
          <Register />
        </GuestRoute>
      </ErrorBoundary>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <ErrorBoundary>
        <GuestRoute>
          <ForgotPassword />
        </GuestRoute>
      </ErrorBoundary>
    ),
  },
  {
    path: '*',
    element: (
      <ErrorBoundary>
        <Layout><NotFoundPage /></Layout>
      </ErrorBoundary>
    ),
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);